import apiClient from "./api";

const PUBLIC_KEY =
  process.env.REACT_APP_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY;

export const paymentsApi = {
  /** Standard checkout — POST /api/create-order */
  createOrder: async (payload) => {
    const response = await apiClient.post("/create-order", payload);
    return response.data;
  },

  /** Standard checkout — POST /api/verify-payment */
  verifyPayment: async (payload) => {
    const response = await apiClient.post("/verify-payment", payload);
    return response.data;
  },

  /** Legacy paths (authenticated) */
  createRazorpayOrder: async (payload) => {
    const response = await apiClient.post("/payments/razorpay/order", payload);
    return response.data;
  },
  verifyRazorpayPayment: async (payload) => {
    const response = await apiClient.post("/payments/razorpay/verify", payload);
    return response.data;
  },
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Open Razorpay Standard Checkout and wait until success, failure, or dismiss.
 * Resolves only once (guards against duplicate handler / payment.failed events).
 */
export const openRazorpayCheckout = async ({
  order,
  keyId,
  customer,
  onSuccess,
  onDismiss,
  onFailure,
  description = "Locsy payment",
}) => {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    throw new Error("Razorpay SDK failed to load");
  }

  const resolvedKey = keyId || PUBLIC_KEY;
  if (!resolvedKey) {
    throw new Error("Razorpay key is not configured");
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const settleOnce = (fn) => (...args) => {
      if (settled) return;
      settled = true;
      fn(...args);
    };

    const options = {
      key: resolvedKey,
      amount: order.amount,
      currency: order.currency || "INR",
      name: "Locsy",
      description: description || "Locsy payment",
      order_id: order.id || order.order_id,
      prefill: {
        name: customer?.name || "",
        contact: customer?.phone || "",
        email: customer?.email || "",
      },
      handler: settleOnce(async (response) => {
        try {
          if (onSuccess) {
            await onSuccess(response);
          }
          resolve(response);
        } catch (err) {
          reject(err);
        }
      }),
      modal: {
        ondismiss: settleOnce(() => {
          if (onDismiss) onDismiss();
          reject(new Error("Payment cancelled"));
        }),
      },
    };

    const razorpay = new window.Razorpay(options);

    razorpay.on(
      "payment.failed",
      settleOnce((response) => {
        const err = response?.error;
        const message =
          err?.description ||
          err?.reason ||
          "Payment failed. Please try again.";
        const error = new Error(message);
        if (onFailure) onFailure(error, response);
        reject(error);
      }),
    );

    razorpay.open();
  });
};

/**
 * End-to-end Standard Checkout: create order → modal → verify signature on server.
 * @param {number} amountPaise - amount in paise (min 100)
 */
export const runStandardRazorpayCheckout = async ({
  amountPaise,
  currency = "INR",
  receipt,
  billId,
  customer,
  description = "Locsy payment",
  onVerified,
  onDismiss,
  onFailure,
}) => {
  const paise = Math.floor(Number(amountPaise));
  if (!Number.isFinite(paise) || paise < 100) {
    throw new Error("Amount must be at least ₹1.00 (100 paise)");
  }

  const orderRes = await paymentsApi.createOrder({
    amount: paise,
    currency,
    receipt,
    billId,
  });

  if (!orderRes?.success) {
    throw new Error(orderRes?.message || "Could not create payment order");
  }

  const orderId = orderRes.order_id || orderRes.order?.id;
  const order = orderRes.order || {
    id: orderId,
    amount: orderRes.amount,
    currency: orderRes.currency,
  };

  const rzResponse = await openRazorpayCheckout({
    order,
    keyId: orderRes.keyId,
    customer,
    description,
    onDismiss,
    onFailure,
  });

  const verifyRes = await paymentsApi.verifyPayment({
    razorpay_order_id: rzResponse.razorpay_order_id,
    razorpay_payment_id: rzResponse.razorpay_payment_id,
    razorpay_signature: rzResponse.razorpay_signature,
  });

  if (!verifyRes?.success || !verifyRes?.verified) {
    throw new Error(verifyRes?.message || "Payment could not be verified");
  }

  const result = {
    checkout: rzResponse,
    verify: verifyRes,
    orderId,
  };
  if (onVerified) onVerified(result);
  return result;
};
